// src/components/ndr-management/NDRTable.jsx
import React from "react";
import { format, parseISO, formatDistanceToNow } from "date-fns";
import { toZonedTime } from "date-fns-tz";

// NDR Status Code Mapping
const NDR_STATUS_MAP = {
  "0": "Unknown Exception",
  "1": "Customer Unavailable",
  "2": "Rejected by Customer",
  "3": "Delivery Rescheduled",
  "4": "No Attempt",
  "5": "Customer Unreachable",
  "6": "Address Issue",
  "7": "Payment Issue",
  "8": "Out Of Delivery Area",
  "9": "Order Already Cancelled",
  "10": "Self Collect",
  "11": "Shipment Seized By Customer",
  "12": "Customer wants open delivery",
  "13": "Shipment Misrouted by logistics partner",
  "14": "OTP-based cancellation",
  "15": "OTP mismatch or unavailable",
};

// NDR Management Status Mapping
const NDR_MANAGEMENT_STATUS_MAP = {
  "100": "Pending Action",
  "200": "Initiated Action",
  "300": "Closed",
  "9999": "Unknown Status",
};

// Order Status Color Mapping
const ORDER_STATUS_COLORS = {
  // Pickup statuses
  "ORDER_PLACED": "bg-blue-100 text-blue-800",
  "AWB_REGISTERED": "bg-blue-100 text-blue-800",
  "PICKUP_PENDING": "bg-yellow-100 text-yellow-800",
  "OUT_FOR_PICKUP": "bg-yellow-100 text-yellow-800",
  "PICKUP_FAILED": "bg-red-100 text-red-800",
  
  // In transit statuses
  "INTRANSIT": "bg-indigo-100 text-indigo-800",
  "SHIPMENT_DELAYED": "bg-orange-100 text-orange-800",
  "SHIPMENT_HELD": "bg-orange-100 text-orange-800",
  
  // Delivery statuses
  "OUT_FOR_DELIVERY": "bg-purple-100 text-purple-800",
  "DELIVERED": "bg-green-100 text-green-800",
  "FAILED_DELIVERY": "bg-red-100 text-red-800",
  
  // RTO statuses
  "RTO_REQUESTED": "bg-red-100 text-red-800",
  "RTO": "bg-red-100 text-red-800",
  "RTO_OUT_FOR_DELIVERY": "bg-red-100 text-red-800",
  "RTO_DELIVERED": "bg-red-100 text-red-800",
  "RTO_FAILED": "bg-red-100 text-red-800",
  "RTO_INTRANSIT": "bg-red-100 text-red-800",
  
  // Other statuses
  "CANCELLED_ORDER": "bg-gray-100 text-gray-800",
  "LOST": "bg-red-100 text-red-800",
  "DAMAGED": "bg-red-100 text-red-800",
};

// Order Type Mapping
const ORDER_TYPE_MAP = {
  "FORWARD": { label: "Forward", color: "bg-blue-100 text-blue-800" },
  "REVERSE": { label: "Reverse", color: "bg-purple-100 text-purple-800" },
  "RTO": { label: "RTO", color: "bg-red-100 text-red-800" },
  "COD": { label: "COD", color: "bg-green-100 text-green-800" },
  "PREPAID": { label: "Prepaid", color: "bg-indigo-100 text-indigo-800" },
};

// Currency Symbol Mapping
const CURRENCY_SYMBOLS = {
  "INR": "₹",
  "USD": "$",
  "EUR": "€",
  "GBP": "£",
  "AED": "د.إ",
  "SAR": "﷼",
};

export default function NDRTable({
  ndrRecords,
  onActionClick,
  onDetailsClick,
}) {
  // Log first record to debug
  React.useEffect(() => {
    if (ndrRecords.length > 0) {
      console.log("First NDR Record:", ndrRecords[0]);
      console.log("NDR Actions:", ndrRecords[0].ndr_actions);
    }
  }, [ndrRecords]);

  // Convert UTC to IST for display with relative time
  const formatDateWithRelative = (dateString) => {
    if (!dateString) return "—";
    try {
      const utcDate = parseISO(dateString);
      const istDate = toZonedTime(utcDate, 'Asia/Kolkata');
      const formatted = format(istDate, "dd MMM yyyy, HH:mm");
      const relative = formatDistanceToNow(istDate, { addSuffix: true });
      return (
        <div className="flex flex-col">
          <span>{formatted}</span>
          <span className="text-xs text-gray-500">{relative}</span>
        </div>
      );
    } catch {
      return dateString;
    }
  };

  const getStatusBadge = (statusCode) => {
    const statusMap = {
      "100": "bg-yellow-100 text-yellow-800",
      "200": "bg-blue-100 text-blue-800",
      "300": "bg-gray-100 text-gray-800",
      "9999": "bg-red-100 text-red-800",
    };
    return statusMap[statusCode] || "bg-gray-100 text-gray-800";
  };

  const getOrderStatusColor = (statusCode) => {
    return ORDER_STATUS_COLORS[statusCode] || "bg-gray-100 text-gray-800";
  };

  const getNDRStatusLabel = (code) => {
    return NDR_STATUS_MAP[code] || code;
  };

  const getNDRManagementStatusLabel = (code) => {
    return NDR_MANAGEMENT_STATUS_MAP[code] || code;
  };

  const getCurrencySymbol = (currencyCode) => {
    return CURRENCY_SYMBOLS[currencyCode] || currencyCode;
  };

  const getOrderTypeDisplay = (orderType) => {
    if (!orderType) return null;
    
    const typeInfo = ORDER_TYPE_MAP[orderType.toUpperCase()];
    
    if (typeInfo) {
      return (
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${typeInfo.color}`}>
          {typeInfo.label}
        </span>
      );
    }
    
    // Fallback for unmapped order types
    return (
      <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
        {orderType}
      </span>
    );
  };

  const handleOrderReferenceClick = (orderReference) => {
    const url = `${window.location.origin}/shipment-details/${orderReference}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleReattemptClick = (ndr) => {
    onActionClick(ndr, "REATTEMPT");
  };

  const handleInitiateRTOClick = (ndr) => {
    onActionClick(ndr, "INITIATE_RTO");
  };

  return (
    <div className="overflow-x-auto bg-white rounded-lg shadow">
      <table className="w-full table-auto">
        <thead className="bg-gray-50 border-b">
          <tr>
            <th className="p-3 text-left font-semibold text-gray-700">
              Order Details
            </th>
            <th className="p-3 text-left font-semibold text-gray-700">
              AWB / Carrier
            </th>
            <th className="p-3 text-left font-semibold text-gray-700">
              Delivery Address
            </th>
            <th className="p-3 text-left font-semibold text-gray-700">
              Current Status
            </th>
            <th className="p-3 text-left font-semibold text-gray-700">
              NDR Details
            </th>
            <th className="p-3 text-left font-semibold text-gray-700">
              NDR Management Status
            </th>
            <th className="p-3 text-left font-semibold text-gray-700">
              OFD Attempts
            </th>
            <th className="p-3 text-left font-semibold text-gray-700">
              NDR Created (IST)
            </th>
            <th className="p-3 text-left font-semibold text-gray-700 sticky right-0 bg-gray-50">
              Actions
            </th>
          </tr>
        </thead>
        <tbody>
          {ndrRecords.map((ndr, index) => {
            // Show buttons based on NDR Management Status
            // Status 100 = Pending Action - show both Reattempt and Initiate RTO
            // Status 200 = Initiated Action - show only View Details
            // Status 300 = Closed - show only View Details
            const isPending = ndr.ndr_management_status_code === "100";
            
            return (
              <tr
                key={`${ndr.order_reference_number}-${index}`}
                className="border-b hover:bg-gray-50"
              >
                <td className="p-3">
                  <div className="flex flex-col gap-1">
                    <span className="text-xs text-gray-500">
                      Order Reference Number
                    </span>
                    <button
                      onClick={() => handleOrderReferenceClick(ndr.order_reference_number)}
                      className="font-medium text-blue-600 hover:text-blue-800 underline text-left"
                    >
                      {ndr.order_reference_number}
                    </button>
                    
                    {/* Order Type Badge */}
                    {ndr.order_type && (
                      <div className="mt-1">
                        {getOrderTypeDisplay(ndr.order_type)}
                      </div>
                    )}
                    
                    {/* Invoice Value with Currency Symbol */}
                    {ndr.order_invoice_value && (
                      <span className="text-xs text-gray-600 mt-1 font-medium">
                        Invoice: {getCurrencySymbol(ndr.order_invoice_currency)}{ndr.order_invoice_value}
                      </span>
                    )}
                  </div>
                </td>

                <td className="p-3">
                  <div className="flex flex-col">
                    <span className="font-medium">{ndr.awb_number || "—"}</span>
                    <span className="text-sm text-gray-600">
                      {ndr.carrier_partner_public_name || "—"}
                    </span>
                  </div>
                </td>

                <td className="p-3">
                  <div className="flex flex-col text-sm">
                    {ndr.delivery_address ? (
                      <>
                        <span className="font-medium">
                          {ndr.delivery_address.drop_poc}
                        </span>
                        <span className="text-gray-600">
                          {ndr.delivery_address.drop_city},{" "}
                          {ndr.delivery_address.drop_state}
                        </span>
                        <span className="text-gray-500">
                          {ndr.delivery_address.drop_postal_code}
                        </span>
                      </>
                    ) : (
                      <span>—</span>
                    )}
                  </div>
                </td>

                <td className="p-3">
                  <div className="flex flex-col gap-2">
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium inline-block w-fit ${getOrderStatusColor(
                        ndr.current_order_status
                      )}`}
                    >
                      {ndr.current_order_status_description_external || ndr.current_order_status || "—"}
                    </span>
                    {ndr.current_order_status_current_location && (
                      <div className="text-xs text-gray-600">
                        <span className="font-medium">Current Location:</span>
                        <span className="ml-1">{ndr.current_order_status_current_location}</span>
                      </div>
                    )}
                    {ndr.current_order_status_remarks && (
                      <div className="text-xs text-gray-600">
                        <span className="font-medium">Remarks From Carrier:</span>
                        <span className="ml-1">{ndr.current_order_status_remarks}</span>
                      </div>
                    )}
                  </div>
                </td>

                <td className="p-3">
                  <div className="flex flex-col text-sm">
                    <span className="font-medium">
                      {getNDRStatusLabel(ndr.latest_ndr_status_code)}
                    </span>
                    <span className="text-xs text-gray-500 mt-1">Last Updated:</span>
                    <div className="text-xs">
                      {formatDateWithRelative(ndr.latest_ndr_status_date_time_utc)}
                    </div>
                  </div>
                </td>

                <td className="p-3">
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge(
                      ndr.ndr_management_status_code
                    )}`}
                  >
                    {getNDRManagementStatusLabel(ndr.ndr_management_status_code)}
                  </span>
                </td>

                <td className="p-3 text-center">
                  <span className="text-lg font-semibold text-gray-700">
                    {ndr.out_for_delivery_attempts_count || 0}
                  </span>
                </td>

                <td className="p-3 text-sm text-gray-600">
                  {formatDateWithRelative(ndr.ndr_creation_date_time_utc)}
                </td>

                <td className="p-3 sticky right-0 bg-white">
                  <div className="flex flex-col gap-2">
                    <button
                      onClick={() => onDetailsClick(ndr)}
                      className="px-3 py-1.5 bg-blue-500 text-white rounded text-sm hover:bg-blue-600 transition-colors whitespace-nowrap"
                    >
                      View Details
                    </button>
                    
                    {/* Show action buttons only for Pending Action status */}
                    {isPending && (
                      <>
                        <button
                          onClick={() => handleReattemptClick(ndr)}
                          className="px-3 py-1.5 bg-green-500 text-white rounded text-sm hover:bg-green-600 transition-colors whitespace-nowrap"
                        >
                          Reattempt
                        </button>
                        <button
                          onClick={() => handleInitiateRTOClick(ndr)}
                          className="px-3 py-1.5 bg-orange-500 text-white rounded text-sm hover:bg-orange-600 transition-colors whitespace-nowrap"
                        >
                          Initiate RTO
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}